import { questionQualityVisibleHomes } from "./confidence"
import type {
  QuestionQualityCheck,
  QuestionQualityField,
  QuestionQualityHistory,
  QuestionQualitySnapshot,
} from "./types"
import {
  variationKindFor,
  VARIATION_BOTH_ASK,
} from "./variation-ask"

/** Same floor as `preferVary`: keep the item, write a new instance. */
export const ALTERNATE_FORM_USED_MIN = 3

/**
 * Seed for Ask Leo and `parseVariationAsk`. Keep the blueprint cell.
 * Do not frame this as a bank swap.
 */
export const ALTERNATE_FORM_ASK =
  "Draft another form of this item. Keep the same blueprint cell."

export type LeoAuthoringJobId =
  | "stem"
  | "options"
  | "rationale"
  | "classification"
  | "alternate-form"
  | "variation-stem"
  | "variation-options"
  | "variation-both"

export type LeoAuthoringJob = {
  id: LeoAuthoringJobId
  category: string
  detail: string
  label: string
  reason?: string
  count?: number
  prompt: string
  focusCheckId?: string
}

const JOB_CATEGORY: Record<LeoAuthoringJobId, string> = {
  stem: "Stem",
  options: "Distractors",
  rationale: "Rationale",
  classification: "Classification",
  "alternate-form": "Usage",
  "variation-stem": "Variation",
  "variation-options": "Variation",
  "variation-both": "Variation",
}

/** Authoring vocabulary in Leo notes — stem, distractors, not composer chrome. */
const LEO_JOB_NOTE_FIELD = {
  stem: "stem",
  distractors: "distractors",
  rationale: "rationale",
  classification: "classification",
} as const

const AUTHORING_JOB_ORDER: Array<
  Exclude<LeoAuthoringJobId, "alternate-form">
> = ["stem", "options", "rationale", "classification"]

const JOB_PROMPT: Record<
  Exclude<LeoAuthoringJobId, "alternate-form">,
  string
> = {
  stem: "Help me with the stem. Keep this item's blueprint cell.",
  options: "Help me with the distractors. Keep this item's blueprint cell.",
  rationale: "Help me add a rationale for the key.",
  classification: "Help me set Bloom and difficulty.",
}

type SpotKind =
  | "incomplete-stem"
  | "missing-passage"
  | "stem-enhancement"
  | "all-and-none"
  | "unstated-options"
  | "distractor-issue"
  | "missing-rationale"
  | "classification"

function counted(n: number, one: string, many: string): string {
  return n === 1 ? `1 ${one}` : `${n} ${many}`
}

function spotKindForCheck(check: QuestionQualityCheck): SpotKind {
  if (
    check.id === "compliance-incomplete-stem" ||
    check.id === "clarity-fragment-stem"
  ) {
    return "incomplete-stem"
  }
  if (check.id === "compliance-missing-reference") return "missing-passage"
  if (check.id === "distractor-aota-nota" || check.id === "distractor-keyed-nota") {
    return "all-and-none"
  }
  if (check.id.startsWith("distractor-unstated-")) return "unstated-options"
  if (check.field === "rationale") return "missing-rationale"
  if (check.field === "settings") return "classification"
  if (check.field === "option" || check.field === "options") {
    return "distractor-issue"
  }
  return "stem-enhancement"
}

function spotKindForHomes(homes: QuestionQualityCheck[]): SpotKind {
  const kinds = new Set(homes.map(spotKindForCheck))
  if (kinds.size === 1) return [...kinds][0]!
  if (homes.every((home) => home.field === "option" || home.field === "options")) {
    return "distractor-issue"
  }
  if (
    homes.every(
      (home) =>
        home.field === "leadIn" ||
        home.field === "title" ||
        home.field === "references",
    )
  ) {
    return "stem-enhancement"
  }
  return "stem-enhancement"
}

/**
 * What Leo found in this category. Count when it adds information.
 * Category is a separate column, so do not repeat Stem or Options here.
 */
export function leoJobDetail(
  id: Exclude<LeoAuthoringJobId, "alternate-form">,
  homes: QuestionQualityCheck[],
): string {
  const n = homes.length
  const kind = spotKindForHomes(homes)
  switch (kind) {
    case "incomplete-stem":
      return counted(n, "incomplete", "incomplete")
    case "missing-passage":
      return counted(n, "missing passage", "missing passages")
    case "stem-enhancement":
      return counted(n, "enhancement", "enhancements")
    case "all-and-none":
      return counted(
        n === 1 && homes[0]?.id === "distractor-aota-nota" ? 2 : n,
        "All and None",
        "All and None",
      )
    case "unstated-options":
      return counted(n, "unstated", "unstated")
    case "distractor-issue":
      return counted(n, "issue", "issues")
    case "missing-rationale":
      return counted(n, "missing", "missing")
    case "classification":
      return counted(n, "unset", "unset")
    default:
      return counted(n, `${id} enhancement`, `${id} enhancements`)
  }
}

export function leoJobChipLabel(
  id: Exclude<LeoAuthoringJobId, "alternate-form">,
  homes: QuestionQualityCheck[],
): string {
  return `${JOB_CATEGORY[id]}. ${leoJobDetail(id, homes)}`
}

function jobIdForField(
  field: QuestionQualityField,
): Exclude<LeoAuthoringJobId, "alternate-form"> | null {
  switch (field) {
    case "leadIn":
    case "title":
    case "references":
      return "stem"
    case "option":
    case "options":
      return "options"
    case "rationale":
      return "rationale"
    case "settings":
      return "classification"
    default:
      return null
  }
}

/**
 * Evidence door only. Low PBI is repair, not a new form.
 * Swap from bank is a later use case. Do not invent it here.
 */
export function shouldOfferAlternateForm(
  history: QuestionQualityHistory | null | undefined,
): boolean {
  if (!history?.sat) return false
  if (history.belowReviewFloor) return false
  return (history.usedCount ?? 0) >= ALTERNATE_FORM_USED_MIN
}

export function alternateFormReason(
  history: QuestionQualityHistory,
): string {
  const n = history.usedCount ?? 0
  const times = n === 1 ? "1 time" : `${n} times`
  if (history.usedInSameCourse) return `Used ${times} in this course`
  return `Used ${times}`
}

export function alternateFormAsk(
  history: QuestionQualityHistory | null | undefined,
): string {
  if (!history || !shouldOfferAlternateForm(history)) return ALTERNATE_FORM_ASK
  return `${ALTERNATE_FORM_ASK} This item was ${alternateFormReason(history).toLowerCase()}.`
}

function alternateFormDetail(history: QuestionQualityHistory): string {
  return alternateFormReason(history)
}

export function leoAuthoringJobs(
  checks: QuestionQualityCheck[],
  history: QuestionQualityHistory | null | undefined,
  snapshot?: QuestionQualitySnapshot,
): LeoAuthoringJob[] {
  const groups = new Map<
    Exclude<LeoAuthoringJobId, "alternate-form">,
    QuestionQualityCheck[]
  >()
  for (const home of questionQualityVisibleHomes(checks, "mark", snapshot)) {
    const id = jobIdForField(home.field)
    if (!id) continue
    const list = groups.get(id) ?? []
    list.push(home)
    groups.set(id, list)
  }

  const jobs: LeoAuthoringJob[] = []
  for (const id of AUTHORING_JOB_ORDER) {
    const list = groups.get(id)
    if (!list?.length) continue
    const detail = leoJobDetail(id, list)
    jobs.push({
      id,
      category: JOB_CATEGORY[id],
      detail,
      label: `${JOB_CATEGORY[id]}. ${detail}`,
      count: list.length,
      prompt: JOB_PROMPT[id],
      focusCheckId: list[0]?.id,
    })
  }

  if (history && shouldOfferAlternateForm(history)) {
    const detail = alternateFormDetail(history)
    jobs.push({
      id: "alternate-form",
      category: JOB_CATEGORY["alternate-form"],
      detail,
      label: `${JOB_CATEGORY["alternate-form"]}. ${detail}`,
      prompt: alternateFormAsk(history),
    })
  }

  if (jobs.length === 0 && snapshot) {
    const quiet = quietVariationJob(snapshot)
    if (quiet) jobs.push(quiet)
  }

  return jobs
}

function quietVariationJob(snapshot: QuestionQualitySnapshot): LeoAuthoringJob {
  const kind = variationKindFor(snapshot)
  if (kind === "both") {
    return {
      id: "variation-both",
      category: JOB_CATEGORY["variation-both"],
      detail: "Stem and distractors",
      label: "Variation. Stem and distractors",
      prompt: `${VARIATION_BOTH_ASK}. Keep this item's blueprint cell.`,
    }
  }
  if (kind === "options") {
    return {
      id: "variation-options",
      category: JOB_CATEGORY["variation-options"],
      detail: "Distractors",
      label: "Variation. Distractors",
      prompt: "Give me different distractors. Keep this item's blueprint cell.",
    }
  }
  return {
    id: "variation-stem",
    category: JOB_CATEGORY["variation-stem"],
    detail: "Wording",
    label: "Variation. Wording",
    prompt: "Give me another wording. Keep this item's blueprint cell.",
  }
}

export function leoJobsAreVariationInviteOnly(jobs: LeoAuthoringJob[]): boolean {
  return (
    jobs.length > 0 &&
    jobs.every((job) => job.id.startsWith("variation-"))
  )
}

/**
 * One note Leo would write. Categories live in the sentence, not a second header.
 */
export function leoJobNote(job: LeoAuthoringJob): string {
  switch (job.id) {
    case "stem":
      if (job.detail.endsWith("incomplete")) {
        return `${job.detail} ${LEO_JOB_NOTE_FIELD.stem}`
      }
      if (job.detail.includes("enhancement")) {
        return job.detail.replace("enhancement", "stem enhancement")
      }
      return `${job.detail} in ${LEO_JOB_NOTE_FIELD.stem}`
    case "options":
      return `${job.detail} in ${LEO_JOB_NOTE_FIELD.distractors}`
    case "rationale":
      if (job.detail.endsWith("missing")) {
        return `${job.detail} ${LEO_JOB_NOTE_FIELD.rationale}`
      }
      return `${job.detail} in ${LEO_JOB_NOTE_FIELD.rationale}`
    case "classification":
      if (job.detail.endsWith("unset")) {
        return `${job.detail} ${LEO_JOB_NOTE_FIELD.classification}`
      }
      return `${job.detail} in ${LEO_JOB_NOTE_FIELD.classification}`
    case "alternate-form":
      return `This item was ${job.detail.toLowerCase()}`
    case "variation-stem":
      return "Want different wording for the question"
    case "variation-options":
      return "Want a different set of distractors"
    case "variation-both":
      return "Want a different stem or distractors"
  }
}

export function leoJobsNote(jobs: LeoAuthoringJob[]): string {
  if (jobs.length === 0) return ""
  const notes = jobs.map(leoJobNote)
  if (leoJobsAreVariationInviteOnly(jobs)) {
    return `No open findings. ${notes[0]}?`
  }
  return `${notes.join(". ")}.`
}
